import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

const rootTouchBufferSx = {
  // Position
  position: 'absolute',
  top: '50%',
  marginTop: '-22px',
  marginLeft: '-22px',

  // Layout
  width: '44px',
  height: '44px',
  padding: 0,
  border: 0,
};

const draggedStyles = {
  transform: 'scale(1.1)',
  boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.3)',
};

const visibleHandleSx = (theme) => ({
  // Position
  position: 'absolute',
  top: '12px',
  marginLeft: '12px',

  // Layout
  width: '20px',
  height: '20px',
  border: `2px solid ${theme.tokens.marketplaceColor}`,
  borderRadius: '50%',
  backgroundColor: theme.tokens.matterColorLight,
  cursor: 'pointer',

  '&:hover': draggedStyles,
});

const visibleHandleDraggedSx = (theme) => ({
  ...visibleHandleSx(theme),
  ...draggedStyles,
});

/**
 * Draggable handle for the RangeSlider.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Overrides the default root class.
 * @param {string} [props.className] - Extra class names for the root element.
 * @param {number} props.min - Minimum allowed value.
 * @param {number} props.max - Maximum allowed value.
 * @param {number} props.value - Current value of the handle.
 * @param {Function} props.valueToPosition - Maps a value to a pixel position.
 * @param {Function} props.positionToValue - Maps a pixel position to a value.
 * @param {Function} props.changeActive - Marks this handle as the active one.
 * @param {Function} props.onChange - Called with the new value while dragging.
 */
const HandleComponent = ({
  rootClassName = null,
  className = null,
  min,
  max,
  value,
  valueToPosition,
  positionToValue,
  changeActive,
  onChange,
}) => {
  const [dragging, setDragging] = useState(false);

  const handleRef = useRef(null);
  const relativePosRef = useRef(null);
  const isMountedRef = useRef(false);
  const draggingRef = useRef(false);

  const setDraggingState = useCallback((next) => {
    draggingRef.current = next;
    setDragging(next);
  }, []);

  const onStart = useCallback(
    (pagePosition) => {
      const { offsetLeft, offsetWidth } = handleRef.current;
      relativePosRef.current = pagePosition - offsetLeft - offsetWidth / 2;
      setDraggingState(true);
      changeActive();
    },
    [changeActive, setDraggingState]
  );

  const onMove = useCallback(
    (pagePosition) => {
      const position = pagePosition - relativePosRef.current;
      const currentValue = positionToValue(position);

      const nextValue = currentValue < min ? min : currentValue > max ? max : currentValue;

      onChange(nextValue);
    },
    [max, min, onChange, positionToValue]
  );

  const onEnd = useCallback(() => {
    // Ensuring that setState doesn't get called.
    // This a strange behaviour since window.removeEventListener is called in componentWillUnmount
    if (isMountedRef.current) {
      setDraggingState(false);
    }
  }, [setDraggingState]);

  const onMouseDown = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      onStart(e.pageX);
    },
    [onStart]
  );
  const onMouseMove = useCallback(
    (e) => {
      if (!draggingRef.current) return;

      onMove(e.pageX);
    },
    [onMove]
  );
  const onMouseUp = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      onEnd();
    },
    [onEnd]
  );

  const onTouchStart = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      onStart(e.touches[0].pageX);
    },
    [onStart]
  );
  const onTouchMove = useCallback(
    (e) => {
      if (!draggingRef.current) return;

      onMove(e.touches[0].pageX);
    },
    [onMove]
  );
  const onTouchEnd = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      onEnd();
    },
    [onEnd]
  );

  useEffect(() => {
    isMountedRef.current = true;
    window.addEventListener('mousemove', onMouseMove, false);
    window.addEventListener('mouseup', onMouseUp, false);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('mousemove', onMouseMove, false);
      window.removeEventListener('mouseup', onMouseUp, false);
    };
  }, [onMouseMove, onMouseUp]);

  const position = valueToPosition(value);

  return (
    <Box
      className={classNames(rootClassName, className)}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
      onTouchStart={onTouchStart}
      ref={handleRef}
      role="button"
      style={{ left: `${position}px` }}
      sx={rootClassName ? undefined : rootTouchBufferSx}
    >
      <Box sx={dragging ? visibleHandleDraggedSx : visibleHandleSx} />
    </Box>
  );
};

HandleComponent.displayName = 'Handle';

const Handle = memo(HandleComponent);

export default Handle;
